import { Application, Controller } from '@hotwired/stimulus';
import { FormFieldsController } from '../../100-components/form-fields/form-fields.stimulus';
import { KeypadController } from '../../100-components/keypad/keypad.stimulus';
import { ScannerController, ScannerState } from '../../100-components/scanner/scanner.stimulus';

type SimpleLoginControllerTarget = typeof SimpleLoginController.targetsKeys;
type Views = 'scan' | 'username' | 'password';

export class SimpleLoginController extends Controller {
    static targetsKeys = [
        'scanView',
        'usernameView',
        'passwordView',
        'passwordTitle',
        'scanner',
        'username',
        'hiddenField',
        'password',
        'keypad',
        'cancel',
        'back',
        'retry',
        'next',
        'error'
    ] as const;

    static targets = [...this.targetsKeys];

    static values = {
        usernameViewUrl: String,
        mediaTouchMode: {
            type: String,
            default: '(pointer: coarse)'
        },
        concatPasswordFieldName: {
            type: String,
            default: 'password_full'
        }
    };

    /**
     * Register the controller in the given application
     */
    static register(application: Application) {
        application.register('simple-login', this);
    }

    /**
     * Scanner/Webcam wrapper
     */
    declare readonly scannerTarget: HTMLInputElement;

    /**
     * Username field
     */
    declare readonly usernameTarget: HTMLInputElement;

    /**
     * A hidden field to declare a username without the username field
     */
    declare readonly hiddenFieldTargets: HTMLInputElement[];

    /**
     * Password field
     */
    declare readonly passwordTarget: HTMLInputElement;

    /**
     * Password fields when multi masked field
     */
    declare readonly passwordTargets: HTMLInputElement[];

    /**
     * Keypad
     */
    declare readonly keypadTarget: HTMLInputElement;

    /**
     * Cancel action
     */
    declare readonly cancelTarget: HTMLButtonElement;

    /**
     * Back action
     */
    declare readonly backTarget: HTMLButtonElement;

    /**
     * Retry action
     */
    declare readonly retryTarget: HTMLButtonElement;

    /**
     * Next action
     */
    declare readonly nextTarget: HTMLButtonElement;

    /**
     * Absolute URL to username view/route for redirection in case
     * user's device does not support or didn't give permission
     * to the camera
     */
    declare readonly usernameViewUrlValue: string;

    /**
     * Whether data-simple-login-username-view-url-value is set or not
     */
    declare readonly hasUsernameViewUrlValue: Boolean;

    /**
     * Media query for the compact mode of the simple login (used for mobile)
     */
    declare readonly mediaTouchModeValue: string;

    /**
     * The field name for hidden password field that contains
     * the concat value (all password character field values combined)
     */
    declare readonly concatPasswordFieldNameValue: string;

    declare readonly scanViewTarget: HTMLElement;
    declare readonly usernameViewTarget: HTMLElement;
    declare readonly passwordViewTarget: HTMLElement;
    declare readonly passwordTitleTarget: HTMLElement;
    declare readonly errorTarget: HTMLElement;
    declare readonly hasErrorTarget: boolean;

    cameraSupported: boolean = false;

    /**
     * Returns true when the media condition in data-media-touch-mode-value matches
     */
    get isTouchMode() {
        return window.matchMedia(this.mediaTouchModeValue).matches;
    }

    async connect() {
        console.log('Connect simple login controller...');
    }

    async scanViewTargetConnected() {
        this.nextTarget.hidden = true;
    }

    usernameViewTargetConnected() {
        this.startInputMode(this.usernameTarget);
    }

    passwordViewTargetConnected() {
        if (!this.hasErrorTarget) {
            this.startInputMode(this.passwordTarget);
        }
    }

    retrieveBarcode({ detail: { value } }) {
        const form = this.element as HTMLFormElement;

        if (value) {
            const url = new URL(value);

            if (form.method && form.method.toUpperCase() === 'GET') {
                window.location.href = url.toString();
            } else {
                form.action = url.toString();
                form.submit();
            }
        }
    }

    addValueAsHiddenField(name, value) {
        const urlValueField = document.createElement('input');
        urlValueField.setAttribute('type', 'hidden');
        urlValueField.setAttribute('name', this.escapeHtml(name));
        urlValueField.setAttribute('value', this.escapeHtml(value));
        urlValueField.setAttribute('data-simple-login-target', 'hiddenField');
        this.element.appendChild(urlValueField);
    }

    removeHiddenFields() {
        this.hiddenFieldTargets.forEach(element => element.remove());
    }

    passwordUpdate(e: Event & { detail: { value: string } }) {
        let value = this.getFullPassword();

        if (value?.length === 3) {
            this.submitLogin();
        }
    }

    retry() {
        this.resetPasswordInput();
    }

    getFullPassword() {
        if (this.passwordTargets.length > 1) {
            return this.passwordTargets.map(passwordTarget => passwordTarget.value).join('');
        } else {
            return this.passwordTarget.value;
        }
    }

    submitLogin() {
        const passwordValue = this.getFullPassword();

        this.addValueAsHiddenField(this.concatPasswordFieldNameValue, passwordValue);

        (this.element as HTMLFormElement).submit();
    }

    updateActions(event: Event & { detail: ScannerState }) {
        switch (event.detail) {
            case ScannerState.NoCamera:
            case ScannerState.NoPermissions:
            case ScannerState.Running:
                this.nextTarget.hidden = false;
                break;
        }
    }

    /**
     * Focuses the field, but on touch devices, does this by clicking the element
     * to enforce the virtual keyboard to appear
     */
    private startInputMode(element: HTMLInputElement) {
        if (this.isTouchMode) {
            element.click();
        } else {
            element.focus();
        }
    }

    private resetPasswordInput() {
        this.passwordTargets.map(input => {
            input.value = '';
            input.removeAttribute('aria-invalid');
            input.removeAttribute('invalid');
            input.removeAttribute('aria-describedby');
        });

        if (this.hasErrorTarget) {
            this.errorTarget.remove();
        }

        this.passwordTarget.focus();
    }

    private escapeHtml(string) {
        const entityMap = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
            '/': '&#x2F;',
            '`': '&#x60;',
            '=': '&#x3D;'
        };

        return String(string).replace(/[&<>"'`=\/]/g, function(s) {
            return entityMap[s];
        });
    }
}
